import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { harvestService } from '../services/harvestService';
import toast from 'react-hot-toast';

export function HarvestOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      
      console.log('🔍 OAuth Callback Debug:', {
        hasCode: !!code,
        hasState: !!state,
        hasError: !!errorParam,
        url: window.location.href
      });
      
      if (errorParam) {
        console.error('❌ OAuth error from Harvest:', errorParam);
        setStatus('error');
        setError(errorParam);
        toast.error(`Harvest authorization failed: ${errorParam}`);
        setTimeout(() => navigate('/settings/connections'), 3000);
        return;
      }
      
      if (!code || !state) {
        console.error('❌ Missing OAuth parameters:', { code, state });
        setStatus('error');
        setError('Missing authorization code or state');
        toast.error('Invalid callback parameters');
        setTimeout(() => navigate('/settings/connections'), 3000);
        return;
      }
      
      console.log('✅ Starting token exchange...');
      
      try {
        await harvestService.completeOAuthFlow(code, state);
        console.log('✅ Token exchange successful!');
        setStatus('success');
        toast.success('Harvest connected successfully!');
        setTimeout(() => navigate('/settings/connections'), 2000);
      } catch (err) {
        console.error('❌ OAuth completion failed:', err);
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Unknown error');
        toast.error('Failed to complete Harvest connection');
        setTimeout(() => navigate('/settings/connections'), 3000);
      }
    };
    
    processCallback();
  }, [searchParams, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connecting to Harvest...
              </h2>
              <p className="text-gray-600">
                Please wait while we complete the authorization.
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Successfully Connected!
              </h2>
              <p className="text-gray-600">
                Redirecting you back to connections...
              </p>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 mb-4">
                {error || 'An unknown error occurred'}
              </p>
              <button
                onClick={() => navigate('/settings/connections')}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Return to Connections
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

